import { defineStore } from 'pinia';
import { ref } from 'vue';
import { USER_STATS_WS_URL } from 'src/logic/constants/user-stats.constants';
import { getClientId } from 'src/logic/services/ws.service';
import { getToken } from 'src/logic/services/token.service';

type StatsConnectionMode = 'anonymous' | 'authorized';

const ANONYMOUS_ID_STORAGE_KEY = 'user-stats-anonymous-id';
const HEARTBEAT_INTERVAL_MS = 1000;
const RECONNECT_DELAY_MS = 5000;

function isOpenOrConnecting(readyState: number): boolean {
  return readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING;
}

export const useUserStatsStore = defineStore('user-stats', () => {
  const isConnected = ref(false);
  const mode = ref<StatsConnectionMode>('anonymous');
  const statsPayload = ref<unknown>(null);

  let ws: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  let heartbeatTimer: number | null = null;
  let activeConnectToken = 0;

  function createAnonymousId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function getOrCreateAnonymousId(): string {
    const storedAnonymousId = sessionStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
    if (storedAnonymousId) return storedAnonymousId;

    const generatedAnonymousId = createAnonymousId();
    sessionStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, generatedAnonymousId);
    return generatedAnonymousId;
  }

  function clearHeartbeat(): void {
    if (heartbeatTimer) {
      window.clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function clearReconnect(): void {
    if (reconnectTimer) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function sendStatsEvent(): void {
    if (ws?.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'stats' }));
  }

  function closeSocket(closeConnection = true): void {
    clearHeartbeat();
    isConnected.value = false;

    if (!ws) return;

    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;

    if (closeConnection && isOpenOrConnecting(ws.readyState)) {
      ws.close();
    }

    ws = null;
  }

  function scheduleReconnect(nextMode: StatsConnectionMode): void {
    if (reconnectTimer) return;

    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      if (nextMode === 'authorized') {
        startAuthorized().then();
      } else {
        startAnonymous();
      }
    }, RECONNECT_DELAY_MS);
  }

  function openSocket(url: string, nextMode: StatsConnectionMode): void {
    if (!USER_STATS_WS_URL) return;

    const canReuseSocket =
      ws && isOpenOrConnecting(ws.readyState) && ws.url === url && mode.value === nextMode;

    if (canReuseSocket) return;

    clearReconnect();
    closeSocket();

    mode.value = nextMode;
    ws = new WebSocket(url);

    ws.onopen = () => {
      isConnected.value = true;
      sendStatsEvent();
      heartbeatTimer = window.setInterval(sendStatsEvent, HEARTBEAT_INTERVAL_MS);
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        statsPayload.value = JSON.parse(event.data);
      } catch {
        statsPayload.value = event.data;
      }
    };

    ws.onclose = () => {
      isConnected.value = false;
      clearHeartbeat();
      ws = null;
      scheduleReconnect(nextMode);
    };

    ws.onerror = () => {
      isConnected.value = false;
    };
  }

  function startAnonymous(): void {
    activeConnectToken += 1;

    if (!USER_STATS_WS_URL) return;

    const anonymousId = getOrCreateAnonymousId();
    const socketUrl = `${USER_STATS_WS_URL}?anonymous_id=${anonymousId}`;
    openSocket(socketUrl, 'anonymous');
  }

  async function startAuthorized(): Promise<void> {
    activeConnectToken += 1;
    const connectToken = activeConnectToken;

    if (!USER_STATS_WS_URL) return;

    const token = getToken();
    if (!token) {
      startAnonymous();
      return;
    }

    try {
      const { client_id } = await getClientId(token);

      if (connectToken !== activeConnectToken) return;

      const socketUrl = `${USER_STATS_WS_URL}?client_id=${client_id}`;
      openSocket(socketUrl, 'authorized');
    } catch {
      if (connectToken !== activeConnectToken) return;
      startAnonymous();
    }
  }

  async function initUserStatsWS(): Promise<void> {
    if (getToken()) {
      await startAuthorized();
      return;
    }

    startAnonymous();
  }

  function stopUserStatsWS(): void {
    activeConnectToken += 1;
    clearReconnect();
    closeSocket();
  }

  return {
    isConnected,
    mode,
    statsPayload,
    initUserStatsWS,
    startAnonymous,
    startAuthorized,
    stopUserStatsWS,
  };
});
